// libraries
import { BaseSelectors } from '@makemydeal/dr-activities-common';

// types
import type { VehiclePackage, WarrantyInfo } from '@makemydeal/dr-sales-checkin-types/types/VehicleDetails';
import type { AdditionalSelectors, VehicleDetailsState, StateTree } from '../types';
import type { FuelEconomyInfoSection, VehicleSpecificationsSection } from './selectors.types';
import type { VppInventoryType, StandardFeatures, InstalledOptionalEquipment } from '@makemydeal/dr-platform-types';

// utils
import { buildImageList } from '../utils';

export class Selectors extends BaseSelectors {
    constructor(public sliceName: string, public additionalSelectors: AdditionalSelectors) {
        super(sliceName);
    }

    getVehicleDetails = (state: StateTree): VehicleDetailsState => {
        return super.getState(state);
    };

    getIsLoading = (state: StateTree): boolean => this.getVehicleDetails(state).isLoading;

    getError = (state: StateTree): string | null => this.getVehicleDetails(state).error;

    // Vehicle specifications section
    getVehicleSpecifications = (state: StateTree): VehicleSpecificationsSection => {
        const details = this.getVehicleDetails(state);
        const msrpValue = this.getMsrpValue(state);

        return {
            vin: this.getVin(state),
            year: details.year,
            make: details.make,
            model: details.model,
            trim: details.trim,
            stockNumber: this.getStockNumber(state),
            chromeId: details.chromeId,
            bodyStyle: details.bodyStyle,
            marketingName: details.marketingName,
            engine: details.engine,
            engineSize: details.engineSize,
            transmission: details.transmission,
            driveLine: details.driveLine,
            fuel: details.fuel,
            exteriorColor: details.exteriorColor,
            exteriorColorFull: details.exteriorColorFull,
            interiorColor: details.interiorColor,
            interiorColorFull: details.interiorColorFull,
            interiorMaterial: details.interiorMaterial,
            odometer: this.getOdometer(state),
            inventoryDate: details.inventoryDate,
            inventoryStatus: details.inventoryStatus,
            inServiceDate: details.inServiceDate,
            dealerId: details.dealerId,
            condition: this.getCondition(state),
            title: details.title,
            msrpValue,
            sellingPriceValue: this.getSellingPrice(state),
            destinationCharge: details.destinationCharge,
            savings: this.getSavings(state),
            vehiclePhotoUrls: buildImageList(details.rawVehiclePhotoUrls, this.additionalSelectors.getVehicle(state)?.vehicleImage)
        };
    };

    getSellingPrice = (state: StateTree): number | undefined => {
        if (this.getCurrentOfferPrice(state)) {
            return this.getCurrentOfferPrice(state);
        }
        return this.getFinancialSellingPriceValue(state);
    };

    // Savings: discount off MSRP when there's a current offer, otherwise MSRP minus selling price
    // [DE768449]: a lead created straight from VinCRM has neither currentOfferPrice nor
    // discountOfMsrp — both arrive only once Manager View saves an offer — so the calculation below
    // is what has to produce the discount until then.
    getSavings = (state: StateTree): number | undefined => {
        const discountOfMsrp = this.getDiscountOfMsrp(state);
        if (this.getCurrentOfferPrice(state) && discountOfMsrp != null) {
            return discountOfMsrp;
        }

        const msrpValue = this.getFinancialMsrpValue(state);
        const sellingPriceValue = this.getFinancialSellingPriceValue(state);

        if (msrpValue != null && sellingPriceValue != null) {
            return msrpValue - sellingPriceValue;
        }

        return undefined;
    };

    // Fuel economy section
    getFuelEconomy = (state: StateTree): FuelEconomyInfoSection => {
        const details = this.getVehicleDetails(state);
        return {
            cityFuelEconomy: details.cityFuelEconomy,
            highwayFuelEconomy: details.highwayFuelEconomy,
            combinedFuelEconomy: details.combinedFuelEconomy,
            cityFuelEconomyEquivalent: details.cityFuelEconomyEquivalent,
            highwayFuelEconomyEquivalent: details.highwayFuelEconomyEquivalent,
            combinedFuelEconomyEquivalent: details.combinedFuelEconomyEquivalent,
            fuel: details.fuel
        };
    };

    // Packages section
    getPackages = (state: StateTree): VehiclePackage[] | undefined => {
        return this.getVehicleDetails(state).packages;
    };

    // Warranties section
    getWarranties = (state: StateTree): WarrantyInfo[] | undefined => {
        return this.getVehicleDetails(state).warranties;
    };

    // In-service date for warranty calculations
    getInServiceDate = (state: StateTree): string | undefined => {
        return this.getVehicleDetails(state).inServiceDate;
    };

    // Vehicle condition (new/used) for warranty visibility
    getCondition = (state: StateTree): VppInventoryType | undefined => {
        const dxgVehicle = this.additionalSelectors.getVehicle(state);
        return this.getVehicleDetails(state).condition ?? dxgVehicle?.condition;
    };

    // VIN and stock number: prefer the API value, fall back to the deal's vehicle
    getVin = (state: StateTree): string | undefined => {
        const dxgVehicle = this.additionalSelectors.getVehicle(state);
        return this.getVehicleDetails(state).vin || dxgVehicle?.vin;
    };

    getStockNumber = (state: StateTree): string | undefined => {
        const dxgVehicle = this.additionalSelectors.getVehicle(state);
        return this.getVehicleDetails(state).stockNumber || dxgVehicle?.stockNumber;
    };

    // Odometer: prefer deal value, fall back to API value
    getOdometer = (state: StateTree): number | undefined => {
        const dxgVehicle = this.additionalSelectors.getVehicle(state);
        return dxgVehicle?.odometerMileage ?? this.getVehicleDetails(state).odometer;
    };

    getFinancialMsrpValue = (state: StateTree): number | undefined => {
        const financialDetails = this.additionalSelectors.getFinancialDetails(state);
        return financialDetails?.priceDetails?.retailPrice;
    };

    getFinancialSellingPriceValue = (state: StateTree): number | undefined => {
        const financialDetails = this.additionalSelectors.getFinancialDetails(state);
        return financialDetails?.priceDetails?.sellingPrice;
    };

    // [DE768449]: a lead from VinCRM carries `baseMsrp` but no `originalMsrp` until Manager View
    // saves, leaving no MSRP to calculate a discount from.
    getMsrpValue = (state: StateTree): number | undefined => {
        const dxgVehicle = this.additionalSelectors.getVehicle(state);
        return dxgVehicle?.priceCostDetails?.originalMsrp ?? dxgVehicle?.priceCostDetails?.baseMsrp;
    };

    getDiscountOfMsrp = (state: StateTree): number | undefined => {
        const dxgVehicle = this.additionalSelectors.getFinancialDetails(state);
        return dxgVehicle?.priceDetails?.discountOfMsrp;
    };

    getCurrentOfferPrice = (state: StateTree): number | undefined => {
        const dxgVehicle = this.additionalSelectors.getFinancialDetails(state);
        return dxgVehicle?.priceDetails?.currentOfferPrice;
    };
    /**
     * Returns true if vehicle details have been fetched and stored.
     * Covers VIN-based responses (vin is set) and chromeStyleId-based responses (chromeId is set).
     */
    getHasVehicleData = (state: StateTree): boolean => {
        const details = this.getVehicleDetails(state);
        return Boolean(details.vin) || Boolean(details.chromeId);
    };

    getStandardFeatures = (state: StateTree): StandardFeatures | undefined => {
        return this.getVehicleDetails(state).standardFeatures;
    };

    getInstalledOptionalEquipment = (state: StateTree): InstalledOptionalEquipment | undefined => {
        return this.getVehicleDetails(state).installedOptionalEquipment;
    };
}
